/**
 * The endpoints the SPA watches (PLAN §10.2's multi-connection model), from
 * the runtime config: the backend that served the page, plus `/config.json`'s
 * `status` block — the stage's cloud and the site's nodes. Each once, keyed by
 * its absolute API URL; relative URLs resolve against the page's origin.
 *
 * A node's WS endpoint is its API URL + `/ws` (the state service's layout;
 * behind the Vite proxy `/api` → `/api/ws`), so the status block names only
 * `apiUrl` for nodes.
 *
 * A config without a `status` block (written before it existed) watches the
 * backend alone.
 */
import type { RuntimeConfig } from '@acp/api-contract/runtimeConfig.js';
import { resolveEndpoints } from '../lib/runtimeConfig.js';

export type EndpointKind = 'cloud' | 'node';

export type Endpoint = {
  /** The absolute API URL: unique per endpoint. */
  readonly id: string;
  readonly kind: EndpointKind;
  readonly label: string;
  readonly apiUrl: string;
  readonly wsUrl: string;
  /** Whether this endpoint served the page (the runtime config's `backend`). */
  readonly servesPage: boolean;
  /**
   * Whether it may be the primary backend (carry the data path). Two things
   * must hold:
   *   - it accepts this page's token: with Cognito every endpoint does (§11), but a
   *     development node's token is its own (a key that node made), and a 401 elsewhere
   *     would sign the operator out;
   *   - the browser lets this page call it: the page's own origin, or the cloud from a
   *     CloudFront page. A node's data routes send no CORS headers yet, and the cloud's
   *     allowlist names only the SPA's domain (and dev ports) — both lists grow with the
   *     site link (phase 4, D16). Until then a peer node or the cloud seen from a node
   *     page is watched (its `/health` is CORS-open) but never failed over to: its
   *     probe would pass while every data call failed.
   */
  readonly canBePrimary: boolean;
};

function nodeWsPath(apiUrl: string): string {
  return `${apiUrl === '' ? '' : apiUrl.replace(/\/$/, '')}/ws`;
}

/** Every watched endpoint, in preference order within each kind: nodes as listed (the serving node first), then the cloud. */
export function watchedEndpoints(config: RuntimeConfig, origin: string): Endpoint[] {
  const own = resolveEndpoints(config.backend, origin);
  const out: Endpoint[] = [];
  const add = (kind: EndpointKind, label: string, apiUrl: string, wsUrl: string): void => {
    const resolved = resolveEndpoints({ kind, label, apiUrl, wsUrl }, origin);
    if (out.some((e) => e.id === resolved.apiUrl)) return;
    const servesPage = resolved.apiUrl === own.apiUrl;
    const callable = new URL(resolved.apiUrl).origin === new URL(origin).origin || (kind === 'cloud' && config.backend.kind === 'cloud');
    const acceptsToken = servesPage || config.auth.kind === 'cognito';
    out.push({ id: resolved.apiUrl, kind, label, apiUrl: resolved.apiUrl, wsUrl: resolved.wsUrl, servesPage, canBePrimary: servesPage || (acceptsToken && callable) });
  };
  const status = config.status;
  // The serving backend is always watched, and a serving node is listed first among the nodes.
  if (config.backend.kind === 'node') add('node', config.backend.label, config.backend.apiUrl, config.backend.wsUrl);
  for (const node of status?.nodes ?? []) add('node', node.label, node.apiUrl, nodeWsPath(node.apiUrl));
  const cloud = status?.cloud ?? null;
  if (config.backend.kind === 'cloud') add('cloud', config.backend.label, config.backend.apiUrl, config.backend.wsUrl);
  else if (cloud !== null) add('cloud', 'Cloud', cloud.apiUrl, cloud.wsUrl);
  return out;
}
